/**
 * @fileOverview The hourly-wage invoicing walkthrough: the three steps of the wizard — project,
 * period, reports — and the dialog that turns the ticked reports into one invoice. Pure data.
 * See docs/view-help.md and docs/view-help/hourlywageinvoice.md.
 */

import { dateToNeutralString } from "@/services/date"
import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpScene, ViewHelpTour } from "@/services/viewHelp/types"

/**
 * Every step of the feature reuses the drawer entry's own gate, which is the pair of invoicing
 * permissions the route is guarded by. One place decides who sees any of this.
 */
const INVOICE = "nav-invoice"

/** A project register is gated by its own pair of "assigned / foreign" rights, as everywhere. */
const view = (suffix: string) =>
  [
    PERMISSIONS[`VIEW_ASSIGNED_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
    PERMISSIONS[`VIEW_FOREIGN_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
  ] as { value: string }[]

/** The sample project the wizard is shown on, and whose registers the last chapter visits. */
const PROJECT = { projectId: "p-nord" }

/**
 * The two days the pointer picks the period with, as `LewDatePicker` anchors every day of its
 * calendar (`date-day-<yyyy-mm-dd>`).
 *
 * The first of the month and today, because those two are the only days the calendar is **certain**
 * to be showing: it opens on the current month, and a day of another month is only drawn when it
 * happens to fall in the first or last week of that month's grid. The sample project reports on
 * every Monday to Thursday of the last six weeks, so the two days span a handful of them — fewer in
 * the first days of a month, which is the one time this walkthrough has little to show.
 */
const today = new Date()
const dayAnchor = (date: Date) => `date-day-${dateToNeutralString(date)}`
const FIRST_OF_MONTH = dayAnchor(new Date(today.getFullYear(), today.getMonth(), 1))
const TODAY = dayAnchor(today)

const onWizard = stepsOn("HourlywageinvoiceWizard", { navEntry: INVOICE })

// The related views: what a report has to carry before it can be billed, where it is written, and
// the alert that sends somebody here. Each is reached by a way (`services/viewHelp/ways.ts`), and its `at` is the
// fallback for a reload.
const onProject = stepsOn("ProjectDetail", { navEntry: INVOICE, at: { params: PROJECT } })
// The settings page itself is open to everybody signed in, so the step carries no gate of its own.
const onSettings = stepsOn("SettingsOverview", { navEntry: INVOICE, at: { hash: "notification" } })

/** A register of the project, opened by its own step pressing its tab. */
const projectTab = (tab: string): ViewHelpScene => ({ key: `tab-${tab}`, enter: [], exit: [] })

/**
 * Billing the hours a site worked, in the order the wizard asks for them: the project, the period,
 * the reports it found, and the dialog that prints one invoice out of the ticked ones.
 *
 * **One scene for the whole demonstration.** Everything after the project is picked depends on
 * everything before it — no period without a project, no list without a search, no dialog without a
 * ticked row — so the steps from the project to the invoice dialog are one scene. A jump onto any of
 * them replays the road at speed and the reader lands on a page that is actually in that state.
 *
 * **Nothing is billed.** "Abrechnung erstellen" writes the invoice, consumes the project's next
 * invoice number and downloads a PDF, so it is pointed at and explained. The dialog's repair is
 * opened and filled in, never saved. The sandbox would refuse all of it anyway — but a save that
 * visibly failed is not what a walkthrough should show.
 */
export const HOURLY_WAGE_INVOICE_TOUR: ViewHelpTour = {
  key: "hourlywageinvoice",
  icon: "mdi-file-document-multiple",
  routes: ["HourlywageinvoiceWizard"],
  // Both directions: what a report needs before it can be billed, where it is written and what
  // billing changes there, and the alert that sends a reader to this page in the first place.
  related: ["ProjectDetail", "SettingsOverview"],
  scenes: [
    // The whole wizard, from the picked project to the open dialog. Its exit closes the dialog and
    // then empties the project field, which is the one control that resets the page — the search,
    // the ticked rows and the calendar's month all hang off it. That is what puts the empty guide
    // back for a reader who jumps to the start again.
    {
      key: "wizard",
      enter: [],
      exit: [{ do: "escape" }, { do: "clear", target: "invoice-project-picker" }],
    },
    projectTab("userPositions"),
    projectTab("materials"),
    projectTab("hourlyWageReports"),
  ],
  steps: [
    // ── Der Assistent ────────────────────────────────────────────────────────
    onWizard("guide", "invoice-guide", "mdi-numeric-3-box-multiple-outline", { placement: "left" }),
    onWizard("project", "invoice-project", "mdi-briefcase-outline", {
      placement: "right",
      scene: "wizard",
      actions: [
        { do: "type", target: "invoice-project-picker", text: "Nord" },
        { do: "choose", text: "Musterprojekt Nord" },
      ],
    }),
    // No zoom: the calendar opens over the whole left column, and its coloured days are the point.
    onWizard("range", "invoice-range", "mdi-calendar-range", {
      placement: "right",
      zoom: false,
      scene: "wizard",
      actions: [
        { do: "click", target: "invoice-range" },
        { do: "click", target: FIRST_OF_MONTH },
        { do: "click", target: TODAY },
      ],
    }),
    onWizard("legend", "invoice-legend", "mdi-circle-multiple-outline", { placement: "right", scene: "wizard" }),
    // The dot sits on the card the press fills rather than on "Suche" itself. The button is at the
    // foot of a sticky column that is taller than the frame, so once the list beside it has made the
    // page long, the button is pinned below the frame's bottom edge and no scroll can bring it back.
    // No zoom either: the press fills the whole card on the right.
    onWizard("search", "invoice-result-head", "mdi-magnify", {
      placement: "right",
      zoom: false,
      scene: "wizard",
      actions: [{ do: "click", target: "invoice-search" }],
    }),
    onWizard("table", "invoice-table", "mdi-table", { placement: "left", scene: "wizard" }),
    onWizard("billed", "invoice-billed", "mdi-checkbox-marked-outline", { placement: "left", scene: "wizard" }),
    // No zoom: the menu ticks rows all down the table, which is what there is to see.
    onWizard("mark", "invoice-mark", "mdi-tune", {
      placement: "bottom",
      zoom: false,
      scene: "wizard",
      actions: [
        { do: "click", target: "invoice-mark" },
        { do: "choose", label: "hourlywageinvoice.overview.card.result.mark.unbilled" },
      ],
    }),
    // The window is shown whole before anything in it is zoomed into.
    onWizard("dialog", "modal-dialog", "mdi-file-document-edit", {
      placement: "bottom",
      scene: "wizard",
      actions: [{ do: "click", target: "invoice-bill" }],
    }),
    // Whichever of the dialog's three alerts the picked reports produce. The blocking one, the list
    // of missing prices, needs an unmaintained record, and the sample company has none — see
    // docs/view-help/hourlywageinvoice.md.
    onWizard("warnings", "invoice-warnings", "mdi-alert-outline", { placement: "bottom", scene: "wizard" }),
    onWizard("options", "invoice-options", "mdi-tune-variant", { placement: "top", scene: "wizard" }),
    // Pointed at, never pressed: it numbers and stores the invoice and downloads its PDF.
    onWizard("create", "invoice-create", "mdi-content-save", { placement: "top", scene: "wizard" }),
    // What that PDF looks like, as the walkthrough cannot print one: pages rendered once by the backend's
    // own generators from the sample world's Musterprojekt Nord. The final invoice and the draft the
    // `options` step offers, next to each other so the difference is seen rather than remembered. Still
    // in the wizard scene, so the dialog stays open behind the pages. See
    // docs/view-help/hourlywageinvoice.md.
    onWizard("print", null, "mdi-file-document-multiple-outline", {
      scene: "wizard",
      images: [
        { src: "view-help/hourlywageinvoice-final.webp", alt: "help.tour.hourlywageinvoice.image.final" },
        { src: "view-help/hourlywageinvoice-draft.webp", alt: "help.tour.hourlywageinvoice.image.draft" },
      ],
    }),

    // Where to find this again — the button that opened it.
    onWizard("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ────────────────────────────────────────────────────────
    //
    // Two registers of the project decide whether a report can be billed at all, the third is where
    // the report is written and where billing leaves its mark, and the bell's threshold is what
    // sends somebody to this page. Each step presses its own tab with the whole page in view.
    onProject("project-positions", "project-user-positions", "mdi-account-cash-outline", {
      placement: "bottom",
      zoom: false,
      scene: "tab-userPositions",
      permissions: view("USERPOSITION"),
      actions: [{ do: "openTab", target: "project-tab-userPositions" }],
    }),
    onProject("project-prices", "project-materials", "mdi-package-variant-closed", {
      placement: "bottom",
      zoom: false,
      scene: "tab-materials",
      permissions: view("MATERIAL"),
      actions: [{ do: "openTab", target: "project-tab-materials" }],
    }),
    onProject("project-reports", "project-hwr", ENTITY_ICONS.hourlywagereport, {
      placement: "bottom",
      zoom: false,
      scene: "tab-hourlyWageReports",
      permissions: view("HOURLYWAGEREPORT"),
      actions: [{ do: "openTab", target: "project-tab-hourlyWageReports" }],
    }),
    onSettings("settings-unbilled", "settings-notify-unbilled", "mdi-bell-alert-outline", { placement: "right" }),
  ],
}
